import * as Schema from "effect/Schema";

import {
  IsoDateTime,
  NonNegativeInt,
  ProjectId,
  ThreadId,
  TrimmedNonEmptyString,
} from "./baseSchemas.ts";

/**
 * A Workflow is a Git-portable graph of deterministic project code in
 * `.afar-research/workflows/<name>/workflow.json`. The user and the agent both
 * edit that file; the Environment Service runs it with chosen parameter values
 * and inputs and keeps every Workflow Run. See ADR 0141.
 */
export const WORKFLOWS_DIRECTORY = ".afar-research/workflows";
export const WORKFLOW_DEFINITION_FILE = "workflow.json";
export const WORKFLOW_DEFINITION_VERSION = 1;
export const WORKFLOW_DEFINITION_MAX_BYTES = 256 * 1024;

const Sha256Digest = Schema.String.check(Schema.isPattern(/^[0-9a-f]{64}$/));

/** A Workflow's folder name, which is also its identity within a project. */
export const WorkflowName = Schema.String.check(
  Schema.isPattern(/^[a-z0-9][a-z0-9._-]{0,63}$/),
).pipe(Schema.brand("WorkflowName"));
export type WorkflowName = typeof WorkflowName.Type;

/** A stable identifier for a node, parameter, or port. */
export const WorkflowIdentifier = Schema.String.check(
  Schema.isPattern(/^[A-Za-z_][A-Za-z0-9_-]{0,63}$/),
);
export type WorkflowIdentifier = typeof WorkflowIdentifier.Type;

const ParameterCommon = {
  name: WorkflowIdentifier,
  label: Schema.optional(Schema.String),
  description: Schema.optional(Schema.String),
};

export const WorkflowParameter = Schema.Union([
  Schema.Struct({
    ...ParameterCommon,
    type: Schema.Literal("number"),
    default: Schema.Finite,
    min: Schema.optional(Schema.Finite),
    max: Schema.optional(Schema.Finite),
    step: Schema.optional(Schema.Finite),
  }),
  Schema.Struct({
    ...ParameterCommon,
    type: Schema.Literal("integer"),
    default: Schema.Int,
    min: Schema.optional(Schema.Int),
    max: Schema.optional(Schema.Int),
  }),
  Schema.Struct({
    ...ParameterCommon,
    type: Schema.Literal("choice"),
    options: Schema.Array(Schema.String).check(Schema.isMinLength(1)),
    default: Schema.String,
  }),
  Schema.Struct({ ...ParameterCommon, type: Schema.Literal("boolean"), default: Schema.Boolean }),
  Schema.Struct({ ...ParameterCommon, type: Schema.Literal("text"), default: Schema.String }),
  Schema.Struct({
    ...ParameterCommon,
    type: Schema.Literal("file"),
    /** A path relative to the checkout root. */
    default: Schema.NullOr(Schema.String),
  }),
]);
export type WorkflowParameter = typeof WorkflowParameter.Type;

/** A parameter's value, as chosen for a run or written as a default. */
export const WorkflowParameterValue = Schema.Union([
  Schema.Finite,
  Schema.Boolean,
  Schema.String,
  Schema.Null,
]);
export type WorkflowParameterValue = typeof WorkflowParameterValue.Type;

/** How the inspector renders an output, and what the node is expected to write. */
export const WorkflowOutputType = Schema.Literals(["text", "metrics", "table", "image", "file"]);
export type WorkflowOutputType = typeof WorkflowOutputType.Type;

export const WorkflowInputPort = Schema.Struct({
  name: WorkflowIdentifier,
  label: Schema.optional(Schema.String),
});
export type WorkflowInputPort = typeof WorkflowInputPort.Type;

export const WorkflowOutputPort = Schema.Struct({
  name: WorkflowIdentifier,
  label: Schema.optional(Schema.String),
  type: WorkflowOutputType,
});
export type WorkflowOutputPort = typeof WorkflowOutputPort.Type;

export const WorkflowNode = Schema.Struct({
  id: WorkflowIdentifier,
  title: Schema.String,
  /**
   * A shell command run from the checkout root. `{{params.<name>}}`,
   * `{{inputs.<port>}}` and `{{outputs.<port>}}` expand to shell-quoted values
   * and file paths.
   */
  command: Schema.String,
  /** Checkout-relative files whose content is part of the node's cache key. */
  codeFiles: Schema.optional(Schema.Array(Schema.String)),
  parameters: Schema.optional(Schema.Array(WorkflowParameter)),
  inputs: Schema.optional(Schema.Array(WorkflowInputPort)),
  outputs: Schema.optional(Schema.Array(WorkflowOutputPort)),
  position: Schema.optional(Schema.Struct({ x: Schema.Finite, y: Schema.Finite })),
});
export type WorkflowNode = typeof WorkflowNode.Type;

export const WorkflowPortRef = Schema.Struct({
  node: WorkflowIdentifier,
  port: WorkflowIdentifier,
});
export type WorkflowPortRef = typeof WorkflowPortRef.Type;

/** Connects one node's output port to another node's input port. */
export const WorkflowEdge = Schema.Struct({ from: WorkflowPortRef, to: WorkflowPortRef });
export type WorkflowEdge = typeof WorkflowEdge.Type;

export const WorkflowDefinition = Schema.Struct({
  version: Schema.Literal(WORKFLOW_DEFINITION_VERSION),
  title: Schema.String,
  description: Schema.optional(Schema.String),
  nodes: Schema.Array(WorkflowNode),
  edges: Schema.optional(Schema.Array(WorkflowEdge)),
});
export type WorkflowDefinition = typeof WorkflowDefinition.Type;

/** Where a run executes: the thread's checkout, or the Primary Checkout without one. */
export const WorkflowCheckout = Schema.Struct({
  projectId: ProjectId,
  threadId: Schema.optional(Schema.NullOr(ThreadId)),
});
export type WorkflowCheckout = typeof WorkflowCheckout.Type;

export const WorkflowCatalogEntry = Schema.Union([
  Schema.Struct({
    status: Schema.Literal("valid"),
    name: WorkflowName,
    digest: Sha256Digest,
    definition: WorkflowDefinition,
  }),
  Schema.Struct({
    status: Schema.Literal("invalid"),
    name: WorkflowName,
    digest: Schema.NullOr(Sha256Digest),
    errors: Schema.Array(Schema.String),
  }),
]);
export type WorkflowCatalogEntry = typeof WorkflowCatalogEntry.Type;

/** A user-supplied input that replaces a wired or missing upstream output. */
export const WorkflowInputOverride = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("file"), path: TrimmedNonEmptyString }),
  Schema.Struct({ kind: Schema.Literal("text"), text: Schema.String }),
]);
export type WorkflowInputOverride = typeof WorkflowInputOverride.Type;

/**
 * Host-local values for the next run, keyed by node id then parameter or port
 * name. A missing parameter falls back to its default.
 */
export const WorkflowRunValues = Schema.Struct({
  parameters: Schema.Record(Schema.String, Schema.Record(Schema.String, WorkflowParameterValue)),
  inputs: Schema.Record(Schema.String, Schema.Record(Schema.String, WorkflowInputOverride)),
});
export type WorkflowRunValues = typeof WorkflowRunValues.Type;

export const EMPTY_WORKFLOW_RUN_VALUES: WorkflowRunValues = { parameters: {}, inputs: {} };

export const WorkflowRunStatus = Schema.Literals([
  "queued",
  "running",
  "completed",
  "failed",
  "cancelled",
  "interrupted",
]);
export type WorkflowRunStatus = typeof WorkflowRunStatus.Type;

export const WorkflowNodeRunStatus = Schema.Literals([
  "pending",
  "running",
  "completed",
  "cached",
  "failed",
  "cancelled",
  "interrupted",
  "skipped",
]);
export type WorkflowNodeRunStatus = typeof WorkflowNodeRunStatus.Type;

export const WorkflowRunOutput = Schema.Struct({
  port: WorkflowIdentifier,
  type: WorkflowOutputType,
  /** Absolute path on the Environment host. */
  path: Schema.String,
  size: NonNegativeInt,
  sha256: Sha256Digest,
});
export type WorkflowRunOutput = typeof WorkflowRunOutput.Type;

export const WorkflowNodeRun = Schema.Struct({
  nodeId: WorkflowIdentifier,
  status: WorkflowNodeRunStatus,
  cacheKey: Schema.NullOr(Sha256Digest),
  /** The run whose outputs a cached node reused. */
  cachedFromRunId: Schema.NullOr(TrimmedNonEmptyString),
  parameters: Schema.Record(Schema.String, WorkflowParameterValue),
  exitCode: Schema.NullOr(Schema.Int),
  error: Schema.NullOr(Schema.String),
  outputs: Schema.Array(WorkflowRunOutput),
  startedAt: Schema.NullOr(IsoDateTime),
  finishedAt: Schema.NullOr(IsoDateTime),
});
export type WorkflowNodeRun = typeof WorkflowNodeRun.Type;

export const WorkflowRunTarget = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("workflow") }),
  Schema.Struct({ kind: Schema.Literal("node"), nodeId: WorkflowIdentifier }),
]);
export type WorkflowRunTarget = typeof WorkflowRunTarget.Type;

export const WorkflowRun = Schema.Struct({
  runId: TrimmedNonEmptyString,
  /** The client's id for the request that queued this run, so a retry finds it instead of queueing twice. */
  requestId: Schema.NullOr(TrimmedNonEmptyString),
  projectId: ProjectId,
  threadId: Schema.NullOr(ThreadId),
  workflowName: WorkflowName,
  /** Absolute checkout root the run executes in, fixed when it is queued. */
  checkoutRoot: Schema.String,
  definitionDigest: Sha256Digest,
  definition: WorkflowDefinition,
  values: WorkflowRunValues,
  target: WorkflowRunTarget,
  force: Schema.Boolean,
  status: WorkflowRunStatus,
  nodes: Schema.Array(WorkflowNodeRun),
  error: Schema.NullOr(Schema.String),
  createdAt: IsoDateTime,
  startedAt: Schema.NullOr(IsoDateTime),
  finishedAt: Schema.NullOr(IsoDateTime),
});
export type WorkflowRun = typeof WorkflowRun.Type;

export class WorkflowError extends Schema.TaggedErrorClass<WorkflowError>()("WorkflowError", {
  message: TrimmedNonEmptyString,
  /** `conflict` when a save's expected digest is no longer the file's digest. */
  reason: Schema.Literals(["not-found", "invalid", "conflict", "unavailable", "busy"]),
}) {}

// RPC payloads

export const WorkflowSubscribeInput = WorkflowCheckout;
export type WorkflowSubscribeInput = typeof WorkflowSubscribeInput.Type;

export const WorkflowStreamEvent = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("catalog"),
    /** Absolute checkout root this catalog was read from. */
    checkoutRoot: Schema.String,
    workflows: Schema.Array(WorkflowCatalogEntry),
  }),
  Schema.Struct({
    type: Schema.Literal("values"),
    workflowName: WorkflowName,
    revision: NonNegativeInt,
    values: WorkflowRunValues,
  }),
  Schema.Struct({ type: Schema.Literal("run"), run: WorkflowRun }),
  Schema.Struct({ type: Schema.Literal("run-removed"), runId: TrimmedNonEmptyString }),
  Schema.Struct({
    type: Schema.Literal("log"),
    runId: TrimmedNonEmptyString,
    nodeId: WorkflowIdentifier,
    chunk: Schema.String,
  }),
]);
export type WorkflowStreamEvent = typeof WorkflowStreamEvent.Type;

export const WorkflowSaveInput = Schema.Struct({
  checkout: WorkflowCheckout,
  name: WorkflowName,
  /** The digest the edit was based on; `null` creates a new Workflow. */
  expectedDigest: Schema.NullOr(Sha256Digest),
  definition: WorkflowDefinition,
});
export type WorkflowSaveInput = typeof WorkflowSaveInput.Type;

export const WorkflowSaveResult = Schema.Struct({ digest: Sha256Digest });
export type WorkflowSaveResult = typeof WorkflowSaveResult.Type;

export const WorkflowSetValuesInput = Schema.Struct({
  projectId: ProjectId,
  name: WorkflowName,
  expectedRevision: NonNegativeInt,
  values: WorkflowRunValues,
});
export type WorkflowSetValuesInput = typeof WorkflowSetValuesInput.Type;

export const WorkflowSetValuesResult = Schema.Struct({ revision: NonNegativeInt });
export type WorkflowSetValuesResult = typeof WorkflowSetValuesResult.Type;

export const WorkflowEnqueueRunInput = Schema.Struct({
  checkout: WorkflowCheckout,
  name: WorkflowName,
  /** Unique per user action; a retried request with the same id returns the run it already queued. */
  requestId: Schema.optional(TrimmedNonEmptyString),
  target: WorkflowRunTarget,
  force: Schema.optional(Schema.Boolean),
  /** Values for this run only; absent uses the stored next-run values. */
  values: Schema.optional(WorkflowRunValues),
});
export type WorkflowEnqueueRunInput = typeof WorkflowEnqueueRunInput.Type;

export const WorkflowRunIdInput = Schema.Struct({ runId: TrimmedNonEmptyString });
export type WorkflowRunIdInput = typeof WorkflowRunIdInput.Type;

export const WorkflowReadOutputInput = Schema.Struct({
  runId: TrimmedNonEmptyString,
  nodeId: WorkflowIdentifier,
  /** An output port name, or `log` for the node's log. */
  port: WorkflowIdentifier,
});
export type WorkflowReadOutputInput = typeof WorkflowReadOutputInput.Type;

export const WORKFLOW_OUTPUT_READ_MAX_BYTES = 4 * 1024 * 1024;

export const WorkflowReadOutputResult = Schema.Struct({
  encoding: Schema.Literals(["utf8", "base64"]),
  mediaType: Schema.String,
  content: Schema.String,
  truncated: Schema.Boolean,
  size: NonNegativeInt,
});
export type WorkflowReadOutputResult = typeof WorkflowReadOutputResult.Type;

// App Control Tool Surface. A tool acts on the calling thread's checkout.

export const WorkflowToolListResult = Schema.Struct({
  workflows: Schema.Array(
    Schema.Struct({
      name: WorkflowName,
      status: Schema.Literals(["valid", "invalid"]),
      title: Schema.NullOr(Schema.String),
      nodeIds: Schema.Array(WorkflowIdentifier),
      errors: Schema.Array(Schema.String),
    }),
  ),
});
export type WorkflowToolListResult = typeof WorkflowToolListResult.Type;

export const WorkflowToolPresentInput = Schema.Struct({
  workflow: WorkflowName,
  /** Focus the card on one node. */
  nodeId: Schema.optional(WorkflowIdentifier),
});
export type WorkflowToolPresentInput = typeof WorkflowToolPresentInput.Type;

export const WorkflowToolPresentResult = Schema.Struct({
  workflow: WorkflowName,
  title: Schema.String,
  nodeIds: Schema.Array(WorkflowIdentifier),
});
export type WorkflowToolPresentResult = typeof WorkflowToolPresentResult.Type;

export const WorkflowToolRunInput = Schema.Struct({
  workflow: WorkflowName,
  /** Run this node and its upstream nodes; absent runs every node. */
  nodeId: Schema.optional(WorkflowIdentifier),
  /** Parameter values by node id, over the stored next-run values. */
  parameters: Schema.optional(
    Schema.Record(Schema.String, Schema.Record(Schema.String, WorkflowParameterValue)),
  ),
  /** Input overrides by node id, over the stored next-run values. */
  inputs: Schema.optional(
    Schema.Record(Schema.String, Schema.Record(Schema.String, WorkflowInputOverride)),
  ),
  /** Re-run every selected node instead of reusing cached outputs. */
  force: Schema.optional(Schema.Boolean),
});
export type WorkflowToolRunInput = typeof WorkflowToolRunInput.Type;

export const WorkflowToolCancelInput = Schema.Struct({ runId: TrimmedNonEmptyString });
export type WorkflowToolCancelInput = typeof WorkflowToolCancelInput.Type;

export const WorkflowToolGetRunInput = Schema.Struct({
  runId: TrimmedNonEmptyString,
  /** Wait up to this many seconds for the run to finish. */
  waitSeconds: Schema.optional(Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 600 }))),
});
export type WorkflowToolGetRunInput = typeof WorkflowToolGetRunInput.Type;

export const WorkflowToolRunResult = Schema.Struct({
  runId: TrimmedNonEmptyString,
  workflow: WorkflowName,
  status: WorkflowRunStatus,
  nodes: Schema.Array(
    Schema.Struct({
      nodeId: WorkflowIdentifier,
      status: WorkflowNodeRunStatus,
      parameters: Schema.Record(Schema.String, WorkflowParameterValue),
      exitCode: Schema.NullOr(Schema.Int),
      error: Schema.NullOr(Schema.String),
      outputs: Schema.Array(
        Schema.Struct({
          port: WorkflowIdentifier,
          type: WorkflowOutputType,
          path: Schema.String,
          /** The start of a text, metrics or table output. */
          preview: Schema.NullOr(Schema.String),
        }),
      ),
      /** The end of the log of a node that failed. */
      logTail: Schema.NullOr(Schema.String),
    }),
  ),
});
export type WorkflowToolRunResult = typeof WorkflowToolRunResult.Type;
